/**
 * Common Schema Definitions
 *
 * Shared branded primitives and object schemas for DRY type definitions.
 * All primitives use branded types for strict type safety.
 */

import { z } from "zod";

// =============================================================================
// Branded Primitive Validators
// =============================================================================

/** UUID string with brand for strict type checking */
export const UuidField = z.uuid().brand<"Uuid">();
export type Uuid = z.infer<typeof UuidField>;

/** Participant ID (client-generated device fingerprint hash) */
export const ParticipantIdField = z.string().min(1).brand<"ParticipantId">();
export type ParticipantId = z.infer<typeof ParticipantIdField>;

/** ISO 8601 datetime with timezone offset */
export const TimestampField = z.iso.datetime({ offset: true }).brand<"Timestamp">();
export type Timestamp = z.infer<typeof TimestampField>;

/** Non-negative integer */
export const NonNegativeIntField = z.number().int().gte(0).brand<"NonNegativeInt">();
export type NonNegativeInt = z.infer<typeof NonNegativeIntField>;

/** Generic metadata object (partial, optional) */
export const MetadataField = z.looseObject({}).optional();
export type Metadata = z.infer<typeof MetadataField>;

// =============================================================================
// Shared Object Schemas
// =============================================================================

/** Standard error response shape */
export const ErrorResponse = z.object({
  success: z.literal(false),
  error: z.string(),
  code: z.string().optional(),
  details: z.object({}).partial().optional(),
});
export type ErrorResponseType = z.infer<typeof ErrorResponse>;

/** Watcher operation types for Durable Object callbacks */
export const WatcherOperation = z.enum(["PUT", "PURGE", "DEL"]);
export type WatcherOperationType = z.infer<typeof WatcherOperation>;

/**
 * Factory for creating watcher data schemas with consistent shape.
 * @param dataSchema - The schema for the `data` array elements
 */
export function createWatcherData<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    operation: WatcherOperation,
    workspaceId: UuidField,
    data: z.array(dataSchema).nullable(),
    timestamp: TimestampField,
  });
}

/** Logical storage bucket keys; each runtime maps exactly these to real bucket names at its boundary (Q137a). */
export const StorageBucketKeySchema = z.enum(["attachments", "messages", "prompts"]);
export type StorageBucketKey = z.infer<typeof StorageBucketKeySchema>;
/** Logical bucket key → real bucket name (exhaustive over StorageBucketKeySchema). */
export const StorageBucketsSchema = z.record(StorageBucketKeySchema, z.string().min(1));
export type StorageBuckets = z.infer<typeof StorageBucketsSchema>;

// =============================================================================
// Field Bundles (for .extend() usage)
// =============================================================================

/** Thread context fields: workspaceId, conversationId, threadId */
export const ThreadContextFields = {
  workspaceId: UuidField,
  conversationId: UuidField,
  threadId: UuidField,
} as const;

/** Standard timestamp fields: createdAt, updatedAt */
export const TimestampsFields = {
  createdAt: TimestampField,
  updatedAt: TimestampField,
} as const;

// =============================================================================
// Exports
// =============================================================================

export const commonSchemas = {
  UuidField,
  ParticipantIdField,
  TimestampField,
  NonNegativeIntField,
  MetadataField,
  ErrorResponse,
  WatcherOperation,
};

export type CommonSchemaTypes = {
  [K in keyof typeof commonSchemas]: z.infer<(typeof commonSchemas)[K]>;
};
